import { useEffect, useState } from 'react'

/**
 * Shared browser connectivity signal. Seeds from `navigator.onLine` and tracks
 * the `window` `online`/`offline` events app-wide (NOT gated to the desktop
 * shell, unlike the old OfflineIndicator).
 *
 * Caveat: `navigator.onLine === true` only means the device has a network
 * interface, not that the backend is reachable. So this drives PROACTIVE UX
 * only (banners, disabling the place-order CTA, the reconnect prompt). The
 * authoritative "did the request actually fail on the wire" signal is the
 * transport-error handling in `api/client.ts` (`ApiClientError.isNetworkError`).
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    // Re-sync once on mount in case the state changed before listeners attached.
    setOnline(navigator.onLine)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  return online
}
